import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@mui/material'
import { ArrowForward } from '@mui/icons-material'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import { apiPost } from '../../generic/Api_Functions'
import { getRandomUUID } from '../../generic/utilities/randomuuid'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { useAuthorization } from '../../generic/hooks/useAuthorization'
import BomImportDialog from './BomImportDialog'
import { BOM_LIBRARY_IMPORT_BEAN } from './bomLibraryImportBean'
import { BOM_STATUS_DRAFT, bomStatusLabel } from './bomLibraryStatus'
import useBomLibraryIndexSetting, { DEFAULT_INDEX_VALUE_WITHOUT_INDEX } from './useBomLibraryIndexSetting'
import { GetDateNumeric } from '../../generic/DateTimeFunctions'

/**
 * BomLibrary — listing of the BOM library (S1, historia 117 HU-01), built like the other RESOURCES
 * lists (`parts/ReferenceTable.jsx`): one `SmartMateriaUITable` over ONE endpoint,
 * `POST bomLibrary/search` (N1), which receives the table's own RequestPagination (generic search,
 * per-column filters, sort, page) exactly as the table builds it. One row per code: the columns read
 * the CURRENT version's fields (`currentVersionNumber`, `currentIndexValue`, `currentCreatedAt`,
 * `componentCount`); Sun already resolves "current" server-side (contrato-sun.md §2).
 *
 * - creation (S14): the table's inline "create" row → `POST bomLibrary` (N8);
 * - import (S28): the table's import button uploads to an `importScopeUUID` this component owns,
 *   then `BomImportDialog` asks for the commit data (N13) or discards the scope (N14);
 * - detail (H134 S29): a row action NAVIGATES to `/resources/bomLibrary/:code`. The `SmartDialog`
 *   that used to host the sheet is gone: the sheet is a page of its own now.
 *
 * Story 134 (H134 S6, S9, S10): the row gains Status (from `overallIndexValue`), Versions, Lines,
 * Created by and Updated.
 *
 * ⚠️ The Status column reads `overallIndexValue`, which summarises ALL the versions of the code, and
 * NEVER `currentIndexValue`. The latter still means "the index of the CURRENT version" and the
 * workflow editor's link dialog depends on exactly that meaning (I-134-8): a BOM that is approved but
 * still holds an old draft has to keep being offered there, and it would silently vanish if the two
 * were merged. That is also why the Status column is NOT sortable (RN-134-10): `overallIndexValue`
 * is not a sort key, and an unknown key degrades silently to `code ASC`.
 */
const BomLibrary = () => {
  const { t_ } = useSmartTranslation()
  const navigate = useNavigate()
  const openSheet = (row) => navigate('/resources/bomLibrary/' + encodeURIComponent(row.code))
  const isUserAuthorized = useAuthorization()
  const canEdit = isUserAuthorized('BOM_LIBRARY_EDIT')
  // PO adjustment 2026-09-10: with USE_WORKFLOW_INDEX off there is no index dimension in the
  // library — no Index Value column and version 1 is born APPROVED without asking (RN-G20).
  const { useIndex } = useBomLibraryIndexSetting()

  const [reloadKey, setReloadKey] = useState(0)
  // Scope of the NEXT upload; a new one is minted once the current one is committed or discarded.
  const [importScopeUUID, setImportScopeUUID] = useState(() => getRandomUUID())
  const [commitScopeUUID, setCommitScopeUUID] = useState('')

  const dataFetch = (pagination) => apiPost('bomLibrary/search', pagination)

  const columns = [
    { name: t_('Code'), field: 'code', mandatory: true, unique: true },
    { name: t_('Name'), field: 'name', mandatory: true },
    { name: t_('Description'), field: 'description' },
    // PO, 2026-09-15: the 'Version' and 'Index Value' columns of story 117 are gone. The functional
    // document lists ONE status column, and two near-synonyms side by side with different values was
    // exactly the confusion R5 warned about. The API key currentIndexValue is untouched (I-134-8):
    // the workflow editor's link dialog filters by it, and that never depended on the column.
    // H134 S6 / S10 - the aggregate status of the code: DRAFT > PROPOSAL > APPROVED over every
    // version. Not sortable (RN-134-10) and not editable: it is computed, never stored.
    ...(useIndex
      ? [{
          name: t_('Status'),
          field: 'overallIndexValue',
          readOnly: true,
          sortable: false,
          // ⚠️ The generic table DISABLES the filter box of a non-sortable column unless it opts in
          // with `searchable`. Without this the whole aggregate filter of D6-bis (built and tested in
          // Sun) was unreachable, and the old Index Value column of #117 did filter: a regression the
          // Judge caught on 2026-09-17 (H134 S22, S24, S25).
          searchable: true,
          type: 'smartSelect',
          isSimpleData: true,
          // ⚠️ NO `filter` property here. In SmartMateriaUITable `column.filter` is a PRESET VALUE
          // for the filter box (`setFilterTextValue`, :1976-1981), not a source of options: an
          // object lands verbatim in `columnFilterValues` and Sun rejects the whole request with a
          // 400, on every load, filtering or not. The filter of this column is the plain text box
          // the generic table already offers (D9, RN-134-09).
          // PO, 2026-09-15: in the LISTING the status is plain text, no chip and no colour (tried both,
          // disliked). The sheet keeps its pills. H134 S3 - no indexed version means an empty cell.
          // Uppercase, so it carries weight in a row of plain text (PO).
          render: (row) => (bomStatusLabel(row.overallIndexValue, t_) || '').toUpperCase()
        }]
      : []),
    // H134 S10 - versionCount stops being a datum that arrives and is never painted.
    // ⚠️ `readOnly` only bites on EXISTING rows in SmartMateriaUITable: on the creation row every
    // column is an input unless it has a `render`. These four are the server's to fill, so they get
    // one and the creation row shows nothing typeable there (PO, 2026-09-15).
    { name: t_('Versions'), field: 'versionCount', readOnly: true, sortable: false, render: (row) => row.versionCount ?? '' },
    { name: t_('Lines'), field: 'componentCount', readOnly: true, sortable: false, render: (row) => row.componentCount ?? '' },
    // H134 S9 / D8 - "Created by", NEVER "Responsible": that figure does not exist in the model.
    // A version created with no user in session travels null and the cell is simply empty.
    { name: t_('Created by'), field: 'currentCreatedBy', readOnly: true, render: (row) => row.currentCreatedBy || '' },
    { name: t_('Updated'), field: 'currentCreatedAt', type: 'datetime', readOnly: true, render: (row) => (row.currentCreatedAt ? GetDateNumeric(row.currentCreatedAt) : '') }
  ]

  const actions = {
    create: {
      disabled: !canEdit,
      // S14 (N8): version 1 with the chosen index (APPROVED, unasked, when the index dimension is
      // off — RN-G20); description optional. The table's alert shows
      // `error.message`, so a rejection is rethrown with the translated business literal
      // (E-BOM-01/02/03/04, contrato-sun.md §4).
      fetchFunction: async (row) => {
        try {
          return await apiPost('bomLibrary', {
            code: row.code || '',
            name: row.name || '',
            description: row.description || '',
            // A BOM created by hand is born as a DRAFT, like every freshly created version of the
            // mockup: with the Index Value column gone there is nothing to pick from the row.
            indexValue: useIndex ? BOM_STATUS_DRAFT : DEFAULT_INDEX_VALUE_WITHOUT_INDEX
          })
        } catch (thrown) {
          // The table's alert reads `message` (text) and `name` (severity) of what is thrown.
          const error = new Error(t_(thrown?.data?.error || 'Not allowed'))
          error.name = thrown?.name || 'warning'
          error.status = thrown?.status
          error.data = thrown?.data
          throw error
        }
      }
    },
    custom: [
      {
        // H134 S29 - opening the sheet is a NAVIGATION now, not a dialog.
        // PO, 2026-09-17: the row's only action is its icon, right there — no kebab menu with one
        // entry. `contextMenu: false` makes the table paint `render` directly, so the click is ours.
        name: 'Open',
        contextMenu: false,
        // Mockup 'MIC Biblioteca de BOMs': a pill "→ Abrir" per row, no column title.
        render: (row) => (
          <Button
            size='small'
            variant='outlined'
            startIcon={<ArrowForward />}
            onClick={() => openSheet(row)}
            sx={{ borderRadius: 999, textTransform: 'none', borderColor: 'divider', color: 'text.primary', px: 1.5, whiteSpace: 'nowrap' }}
          >
            {t_('Open')}
          </Button>
        )
      }
    ]
  }

  const importFile = [{
    bean: BOM_LIBRARY_IMPORT_BEAN,
    name: t_('BOM library'),
    extraParams: new Map([['importScopeUUID', importScopeUUID]]),
    closeDialogOnSuccess: true,
    // The upload only validates and parks the lines in the scope: nothing is persisted until the
    // user commits with code, name and index (N13), or leaves and the scope is discarded (N14).
    // Returning false keeps the dialog from claiming success for a BOM that does not exist yet
    // (PO, 2026-09-15): the commit dialog that opens right after is the real feedback.
    postImportAction: () => {
      setCommitScopeUUID(importScopeUUID)
      return false
    }
  }]

  const finishImport = (reload) => {
    setCommitScopeUUID('')
    setImportScopeUUID(getRandomUUID())
    if (reload) setReloadKey((key) => key + 1)
  }

  return (
    <div data-testid='bom-library'>
      <SmartMateriaUITable
        tableTuneUniqueKey='bomLibrary'
        title={t_('BOM library')}
        columns={columns}
        dataFetch={dataFetch}
        actions={actions}
        importFile={importFile}
        importFileDisabled={!canEdit}
        forceLoad={reloadKey}
        pageable
        primaryKeys={['code']}
        // H134 S19 / S25 - an empty result is EXPLAINED, and neither the term nor the filters are
        // cleared: the user has to be able to see what they asked for and correct it.
        noDataMessage={t_('No BOM matches the current search and filters')}
      />

      {commitScopeUUID && (
        <BomImportDialog
          importScopeUUID={commitScopeUUID}
          onClose={() => finishImport(false)}
          onCommitted={() => finishImport(true)}
        />
      )}
    </div>
  )
}

export default BomLibrary
